import {
  ApiAuditLog,
  ApiClient,
  ApiDevice,
  ApiEmployee,
  ApiTimeEvent,
  ListByClientRequest,
  ListClientDeviceLogsRequest,
  ListLogsRequest,
  ListTimeEventsRequest,
  ProvisionDeviceRequest,
  ProvisionDeviceResponse,
  RegisterClientRequest,
  RevokeDeviceRequest,
  RotateDeviceKeyRequest,
} from './contracts';

const normalizeBaseUrl = (value?: string): string => {
  if (!value) {
    return '';
  }

  return value.replace(/\/+$/, '');
};

const parseTimeout = (value?: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 15000;
};

const API_BASE_URL = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL);
const API_TIMEOUT_MS = parseTimeout(process.env.REACT_APP_API_TIMEOUT_MS);

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider | null = null;

export class ApiError extends Error {
  status: number;
  body: string;

  constructor(message: string, status: number, body = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function setApiTokenProvider(provider: TokenProvider | null) {
  tokenProvider = provider;
}

function buildUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = await tokenProvider?.();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  try {
    return await fetch(buildUrl(path), {
      ...init,
      signal: init?.signal ?? controller.signal,
      headers: {
        Accept: 'application/json, text/plain',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers || {}),
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`API request timed out after ${API_TIMEOUT_MS}ms`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, init);

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new ApiError(text || `Request failed with status ${response.status}`, response.status, text);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export async function checkHealth(): Promise<string> {
  const response = await apiFetch('/admin/health', {
    headers: {
      Accept: 'text/plain',
    },
  });
  if (!response.ok) {
    throw new ApiError(`Health check failed with status ${response.status}`, response.status);
  }
  return response.text();
}

export function listClients(): Promise<ApiClient[]> {
  return request<ApiClient[]>('/admin/api/listClients');
}

export function registerClient(payload: RegisterClientRequest): Promise<ApiClient> {
  return request<ApiClient>('/admin/api/registerClient', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function listDevices(): Promise<ApiDevice[]> {
  return request<ApiDevice[]>('/admin/api/listAllDevices');
}

export function listClientDevices(payload: ListByClientRequest): Promise<ApiDevice[]> {
  return request<ApiDevice[]>('/admin/api/listClientDevices', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function listClientEmployees(payload: ListByClientRequest): Promise<ApiEmployee[]> {
  return request<ApiEmployee[]>('/admin/api/listClientEmployees', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function listClientTimeEvents(payload: ListTimeEventsRequest): Promise<ApiTimeEvent[]> {
  return request<ApiTimeEvent[]>('/admin/api/listClientTimeEvents', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function provisionDevice(payload: ProvisionDeviceRequest): Promise<ProvisionDeviceResponse> {
  return request<ProvisionDeviceResponse>('/admin/api/provisionDevice', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function revokeDevice(payload: RevokeDeviceRequest): Promise<boolean> {
  return request<boolean>('/admin/api/revokeDevice', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function rotateDeviceKey(payload: RotateDeviceKeyRequest): Promise<void> {
  return request<void>('/admin/api/rotateKeys', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function listClientLogs(payload: ListLogsRequest): Promise<ApiAuditLog[]> {
  return request<ApiAuditLog[]>('/admin/api/listClientLogs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function listClientDeviceLogs(payload: ListClientDeviceLogsRequest): Promise<ApiAuditLog[]> {
  return request<ApiAuditLog[]>('/admin/api/listDeviceLogs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export const apiConfig = {
  baseUrl: API_BASE_URL,
  displayBaseUrl: API_BASE_URL || 'same-origin dev proxy',
  timeoutMs: API_TIMEOUT_MS,
};

export interface BiometricStatus { employee_id: string; model_version: string; sample_count: number; status: string }
export function getBiometricStatus(employeeId: string, clientId: string): Promise<BiometricStatus> {
  return request(`/admin/api/employees/${encodeURIComponent(employeeId)}/biometrics?client_id=${encodeURIComponent(clientId)}`);
}
export function revokeBiometrics(employeeId: string, clientId: string): Promise<void> {
  return request(`/admin/api/employees/${encodeURIComponent(employeeId)}/biometrics?client_id=${encodeURIComponent(clientId)}`, {method: 'DELETE'});
}
export function enrollBiometrics(employeeId: string, payload: {client_id: string; consent_recorded_at: string; frames: {step: string; image_base64: string; captured_at_ms: number}[]}): Promise<BiometricStatus> {
  return request(`/admin/api/employees/${encodeURIComponent(employeeId)}/biometrics/enroll`, {method: 'POST', body: JSON.stringify(payload)});
}
